import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  Loader2,
  Mail,
  Phone,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COUNTRY_CODES, INVESTOR_PROFILES } from "@/data/dholera";
import { inr } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-11 rounded-none border-input bg-background font-mono text-sm";

export default function LeadModal({ lead, onClose }) {
  const open = !!lead;
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    profile: "Individual",
  });

  useEffect(() => {
    if (lead) {
      setStep("form");
      setError("");
      setResult(null);
    }
  }, [lead]);

  if (!lead) return null;
  const p = lead.property;
  const isVisit = lead.mode === "visit";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2)
      return setError("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return setError("Please enter a valid email address.");
    if (!/^\d{6,14}$/.test(form.phone))
      return setError("Enter a valid phone number — digits only.");
    setStep("submitting");
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        country_code: form.countryCode,
        phone: form.phone,
        investor_profile: form.profile,
        mode: lead.mode,
        property_id: p.id,
        property_name: p.name,
      });
      setResult(data);
      setStep("success");
    } catch (err) {
      setStep("form");
      setError("Could not record your interest. Please try again.");
      toast.error("Submission failed — please retry.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="overflow-hidden rounded-none border-border p-0 sm:max-w-lg"
        data-testid="lead-modal"
      >
        <DialogTitle className="sr-only">
          {isVisit ? "Book a virtual site visit" : "Express interest"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Leave your details and a Dholera relationship manager is assigned.
        </DialogDescription>

        <div className="border-b border-border bg-secondary/50 px-6 py-4">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
            {isVisit && <Video className="h-3 w-3" />}
            {isVisit ? "Book Virtual Site Visit" : "Express Interest"}
          </p>
          <p className="mt-1.5 font-display text-lg font-medium tracking-tight">
            {p.name}
          </p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            {p.plotSqYd > 0
              ? `${p.zone} · Sector ${p.sector} · ${p.plotSqYd.toLocaleString("en-IN")} sq yd · ${inr(p.pricePerSqYd)}/sq yd`
              : `${p.zone} · ${p.type}`}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {step !== "success" ? (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              onSubmit={submit}
              className="space-y-4 px-6 py-6"
              data-testid="lead-form"
            >
              <div>
                <Label
                  htmlFor="lead-name"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                >
                  Full Name
                </Label>
                <Input
                  id="lead-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Rohan Shah"
                  className={`${inputCls} mt-2`}
                  data-testid="lead-name-input"
                />
              </div>
              <div>
                <Label
                  htmlFor="lead-email"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                >
                  Email
                </Label>
                <Input
                  id="lead-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="rohan@example.com"
                  className={`${inputCls} mt-2`}
                  data-testid="lead-email-input"
                />
              </div>
              <div>
                <Label
                  htmlFor="lead-phone"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
                >
                  Phone
                </Label>
                <div className="mt-2 grid grid-cols-[104px_1fr] gap-2">
                  <Select
                    value={form.countryCode}
                    onValueChange={(v) => setForm({ ...form, countryCode: v })}
                  >
                    <SelectTrigger
                      className={`${inputCls} px-2.5`}
                      data-testid="lead-country-code-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`cc-option-${c.replace("+", "")}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="lead-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })
                    }
                    placeholder="9876543210"
                    className={inputCls}
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Investor Profile
                </Label>
                <Select
                  value={form.profile}
                  onValueChange={(v) => setForm({ ...form, profile: v })}
                >
                  <SelectTrigger
                    className={`${inputCls} mt-2`}
                    data-testid="lead-profile-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {INVESTOR_PROFILES.map((ip) => (
                      <SelectItem key={ip} value={ip} data-testid={`profile-option-${ip.toLowerCase()}`}>
                        {ip}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {error && (
                <p
                  className="border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-xs text-destructive"
                  data-testid="lead-form-error"
                >
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={step === "submitting"}
                className="h-12 w-full rounded-none font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="lead-submit-btn"
              >
                {step === "submitting" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Confirm {isVisit ? "Site Visit" : "Interest"}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                A relationship manager is assigned instantly
              </p>
            </motion.form>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="px-6 py-8"
              data-testid="lead-success"
            >
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
                className="flex h-14 w-14 items-center justify-center rounded-full border border-accent/40 bg-accent/10"
              >
                <Check className="h-6 w-6 text-accent" strokeWidth={3} />
              </motion.div>
              <h3 className="mt-5 font-display text-2xl font-medium uppercase tracking-tight">
                {isVisit ? "Site visit requested." : "Interest recorded."}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Reference{" "}
                <span
                  className="border border-border bg-secondary px-2 py-0.5 font-mono text-xs font-bold"
                  data-testid="lead-ref-id"
                >
                  {result.id.slice(0, 8).toUpperCase()}
                </span>
              </p>

              <div
                className="mt-6 flex items-center gap-4 border border-border p-4"
                data-testid="relationship-manager-card"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-accent font-display text-base font-extrabold text-white">
                  {result.relationship_manager.name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium" data-testid="rm-name">
                    {result.relationship_manager.name}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    {result.relationship_manager.desk}
                  </p>
                </div>
                <div className="hidden shrink-0 text-right font-mono text-[11px] text-muted-foreground sm:block">
                  <p className="flex items-center justify-end gap-1.5">
                    <Phone className="h-3 w-3" />
                    {result.relationship_manager.phone}
                  </p>
                  <p className="mt-1 flex items-center justify-end gap-1.5">
                    <Mail className="h-3 w-3" />
                    {result.relationship_manager.email}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  What happens next
                </p>
                <ol className="mt-3 space-y-2.5 text-sm text-muted-foreground">
                  {[
                    "Your RM calls within 4 business hours to qualify the mandate.",
                    "RERA certificate, TP scheme map and title summary arrive on email.",
                    isVisit
                      ? "A live drone walkthrough of the parcel is scheduled at your slot."
                      : "A virtual or on-ground site walkthrough is scheduled at your convenience.",
                  ].map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-mono text-xs font-bold text-accent">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>

              <Button
                variant="outline"
                onClick={onClose}
                className="mt-8 h-11 w-full rounded-none font-mono text-xs uppercase tracking-[0.2em]"
                data-testid="lead-done-btn"
              >
                Done
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
